import Router from 'next/router';
import axios from 'axios';
import get from 'lodash/get';

import { API, DISTRIBUTOR, DOMAIN } from '@autopass-web/domain';

import { PATH } from '../config';
import storages from '../service/storage';

import type {
  CreateLicensePlateQuery,
  GetBulletinBoardsQuery,
  GetLicensePlateQuery,
  GetOrdersSimpleQuery,
  GetPlusPayEncryptedRequestDataQuery,
  UpdateLicensePlateQuery,
  UpdateUserQuery,
} from './request-types';
import type {
  CancelParkingRecordResponse,
  CreateLicensePlateResponse,
  EstimateOrderResponse,
  GetBootstrapsResponse,
  GetBulletinBoardsResponse,
  GetInvoiceResponse,
  GetLicensePlatesResponse,
  GetOrdersSimpleResponse,
  GetParkingRecordsResponse,
  GetPlusPayEncryptedRequestDataResponse,
  GetUserMeResponse,
  InitMochipayValidationResponse,
  LeaveParkingRecordResponse,
  ProcessInvoiceResponse,
  UpdateLicensePlateResponse,
  UpdateUserMeResponse,
} from './response-types';

const API_CONFIG = DOMAIN.API_URL[API.USER];
const CURRENT_DISTRIBUTOR =
  process.env['NEXT_PUBLIC_DISTRIBUTOR'] || DISTRIBUTOR.AUTOPASS;
const DISTRIBUTOR_CLIENT_UID =
  DOMAIN['DISTRIBUTOR_CLIENT_UID'][CURRENT_DISTRIBUTOR];

const axiosInstance = axios.create({
  headers: API_CONFIG.headers,
  baseURL: API_CONFIG.baseUrl,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

axiosInstance.interceptors.request.use(
  (request) => {
    const tokenInfo = storages.getAuth();

    if (tokenInfo) {
      const accessToken = get(storages.getAuth(), 'accessToken', undefined);
      request.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    request.headers['Mochipay-Client-Id'] = DISTRIBUTOR_CLIENT_UID;
    return request;
  },
  (err) => {
    return Promise.reject(err);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err?.response?.status === 401) {
      Router.push(PATH.EXPIRED);
      storages.clearAuth();
    }
    return Promise.reject(err);
  },
);

class Client {
  fetchBootstraps = () =>
    axiosInstance.get<GetBootstrapsResponse>('/bootstraps');

  fetchUserMe = () => axiosInstance.get<GetUserMeResponse>('/me');

  fetchBulletinBoards = ({
    codes,
    distributor,
    partnerId,
  }: GetBulletinBoardsQuery) =>
    axiosInstance.get<GetBulletinBoardsResponse>('/bulletin_boards', {
      params: {
        codes,
        distributor,
        partner_id: partnerId,
      },
    });

  updateUserMe = ({
    enableOnStreetParkingAutoPay,
    defaultGas,
    agreeTermsFfTaipeiCityParkinglotService,
    receipt,
    enablePoint,
  }: UpdateUserQuery) =>
    axiosInstance.post<UpdateUserMeResponse>('/me', {
      enable_on_street_parking_auto_pay: enableOnStreetParkingAutoPay,
      default_gas: defaultGas,
      agree_terms_of_taipei_city_parkinglot_service:
        agreeTermsFfTaipeiCityParkinglotService,
      receipt,
      enable_point: enablePoint,
    });

  createLicensePlate = ({
    plateNumber,
    vehicleType,
    oilType,
    energySource,
  }: CreateLicensePlateQuery) =>
    axiosInstance.post<CreateLicensePlateResponse>('/license_plates', {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      oil_type: oilType,
      energy_source: energySource,
    });

  updateLicensePlate = ({
    plateNumber,
    vehicleType,
    oilType,
    energySource,
    licensePlateId,
  }: UpdateLicensePlateQuery) =>
    axiosInstance.put<UpdateLicensePlateResponse>(
      `/license_plates/${licensePlateId}`,
      {
        plate_number: plateNumber,
        vehicle_type: vehicleType,
        oil_type: oilType,
        energy_source: energySource,
      },
    );

  deleteLicensePlate = ({ licensePlateId }: { licensePlateId: string }) =>
    axiosInstance.delete(`/license_plates/${licensePlateId}`);

  fetchLicensePlates = ({
    perPage,
    page,
    plateNumberCont,
    plateNumberStart,
    orderBy = 'created_at_desc',
  }: GetLicensePlateQuery = {}) =>
    axiosInstance.get<GetLicensePlatesResponse>('/license_plates', {
      params: {
        per_page: perPage,
        page,
        plate_number_cont: plateNumberCont,
        plate_number_start: plateNumberStart,
        order_by: orderBy,
      },
    });

  bindLicensePlateToCityParkinglot = ({
    licensePlateId,
  }: {
    licensePlateId: string;
  }) =>
    axiosInstance.post<void>(
      `/license_plates/${licensePlateId}/bind_city_parkinglot`,
    );

  fetchParkingRecords = () =>
    axiosInstance.get<GetParkingRecordsResponse>('/parking_records');

  leaveParkingRecord = ({ parkingRecordId }: { parkingRecordId: string }) =>
    axiosInstance.post<CancelParkingRecordResponse>(
      `/parking_records/${parkingRecordId}/leave`,
    );

  cancelParkingRecord = ({ parkingRecordId }: { parkingRecordId: string }) =>
    axiosInstance.post<LeaveParkingRecordResponse>(
      `/parking_records/${parkingRecordId}/cancel`,
    );

  fetchOrdersSimple = ({
    transactionTypes,
    generalizedTransactionTypes,
    plateNumber,
    startAt,
    endAt,
    page,
    perPage,
  }: GetOrdersSimpleQuery) =>
    axiosInstance.get<GetOrdersSimpleResponse>('/orders/simple', {
      params: {
        transaction_types: transactionTypes,
        plate_number: plateNumber,
        generalized_transaction_types: generalizedTransactionTypes,
        start_at: startAt,
        end_at: endAt,
        page,
        per_page: perPage,
      },
    });

  exchangeAccessTokenWithOneTimeToken = ({
    oneTimeToken,
  }: {
    oneTimeToken: string;
  }) =>
    axiosInstance.post<{
      data: {
        access_token: string;
        expires_in: number;
        is_first_time_login: boolean;
        refresh_token: null;
        scopes: 'user';
        should_update_user_info: null;
        user_id: string;
      };
    }>('/one_time_token', {
      one_time_token: oneTimeToken,
    });

  initMochipayValidation = () =>
    axiosInstance.post<InitMochipayValidationResponse>(
      '/wallets/init_mochipay_validation',
    );

  createTransactionToken = ({
    token,
    transactionId,
  }: {
    token: string;
    transactionId: string;
  }) =>
    axiosInstance.post('/wallets/mochipay_wallet', {
      token,
      transaction_id: transactionId,
      default_distributor: true,
    });

  deleteCreditCards = () => axiosInstance.delete('/wallets/credit_cards');

  turnOnWalletBonusPointRedeem = () =>
    axiosInstance.post('/wallets/redeem_bonus_points/enable');

  turnOffWalletBonusPointRedeem = () =>
    axiosInstance.post('/wallets/redeem_bonus_points/disable');

  __plusPayFetchEncryptedDeeplinkRequestData = ({
    userId,
    callbackUrl,
  }: GetPlusPayEncryptedRequestDataQuery) =>
    axiosInstance.get<GetPlusPayEncryptedRequestDataResponse>(
      '/pluspay/encrypted_deeplink_request_data',
      {
        params: {
          user_id: userId,
          callback_url: callbackUrl,
        },
      },
    );

  __plusPayBindWallet = ({ encryptData }: { encryptData: string }) =>
    axiosInstance.post('/pluspay/bind_wallet', {
      encrypt_data: encryptData,
    });

  getInvoice = ({ invoiceId }: { invoiceId: string }) =>
    axiosInstance.get<GetInvoiceResponse>(`/invoices/${invoiceId}`);

  processInvoice = ({
    invoiceId,
    answer,
  }: {
    invoiceId: string;
    answer: 'reject' | 'accept';
  }) =>
    axiosInstance.post<ProcessInvoiceResponse>(`/invoices/${invoiceId}`, {
      answer,
    });

  createEstimatedOrder = ({ invoiceId }: { invoiceId: string }) =>
    axiosInstance.post<EstimateOrderResponse>(
      `/invoices/${invoiceId}/estimated_order_info`,
    );
}

const client = new Client();

export default client;
